import React, { useContext } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import { useListAndSearchAllAccounts } from "@rentablo/api/apiv1";
import { useAccountIdsByTypes } from "@hooks";
import { useSelectGroupsForId } from "../../hooks/useSelectGroupsForId.ts";
import {
  DashboardSelectedGroupsContext,
  DashboardSelectedGroupsContextProps,
} from "./DashboardSelectedGroupsContext";

export const useDashboardSelectedGroupsContext =
  (): DashboardSelectedGroupsContextProps => {
    const context = useContext(DashboardSelectedGroupsContext);
    if (!context) {
      throw new Error(
        "useDashboardSelectedGroups must be used within a DashboardSelectedGroupsProvider"
      );
    }
    return context;
  };

export const DashboardSelectedGroupsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const {
    data: selectedGroup,
    refetch: refetchGroups,
    isFetching: isGroupsLoading,
    isPending: isGroupsPending,
  } = useSelectGroupsForId({
    id: "dashboard",
  });

  const {
    data: accounts,
    isFetching: isAccountsFetching,
    isPending: isAccountsPending,
  } = useListAndSearchAllAccounts(
    { id: selectedGroup?.accountIds },
    {
      enabled: Boolean(!isGroupsLoading && selectedGroup?.accountIds),
      placeholderData: keepPreviousData,
    }
  );

  const groupedAccountIds = useAccountIdsByTypes(accounts?.accounts);

  return (
    <DashboardSelectedGroupsContext.Provider
      value={{
        accountIds: selectedGroup?.accountIds,
        selectedGroup: selectedGroup,
        groupedAccountIds,
        accounts: accounts?.accounts,
        refetch: refetchGroups,
        isFetching: isGroupsLoading || isAccountsFetching,
        isPending: isGroupsPending || isAccountsPending,
      }}
    >
      {children}
    </DashboardSelectedGroupsContext.Provider>
  );
};
